import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import ShoppingCartCheckoutOutlinedIcon from '@mui/icons-material/ShoppingCartCheckoutOutlined';

import { PlanningWorkbenchShell } from '../planningcore';
import PpmGrid from './PpmGrid';
import PpmLinesGrid from './PpmLinesGrid';
import PpmSummaryPanel from './PpmSummaryPanel';
import PpmReportWorkbench from './PpmReportWorkbench';

import PpmSuperactivitiesPanel from './PpmSuperactivitiesPanel';
import PpmSuperactivityDialog from './PpmSuperactivityDialog';
import PpmSuperactivityCreateDialog, {
  PpmSuperactivityCreateModel,
} from './PpmSuperactivityCreateDialog';

import {
  calendarizePpm,
  fetchPpmLookups,
  fetchPpmWorkbench,
  generateDraftPpm,
  savePpmLines,
  transitionPpm,
  assignPpmSuperactivityLines,
  fetchPpmSuperactivityWorkbench,
  createPpmSuperactivity,
} from './procurement.apis';

import {
  PpmWorkbenchModel,
  PpmSuperactivityRow,
  PpmSuperactivityWorkbenchModel,
} from './procurement.models';

type State = {
  model?: PpmWorkbenchModel;
  lookups: any;
  tab: number;
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;
  selectedId?: number | null;
  name: string;
  notes: string;
  dialogOpen: boolean;
  actioncode: string;

  superactivityOpen: boolean;
  superactivityLoading: boolean;
  superactivitySaving: boolean;
  superactivityModel: PpmSuperactivityWorkbenchModel | null;

  superactivityCreateOpen: boolean;
  superactivityCreateSaving: boolean;
};


export default class PpmControlCenter extends React.Component<any, State> {

  state: State = {
    lookups: {},
    tab: 0,
    loading: false,
    saving: false,
    error: '',
    success: '',
    selectedId: null,
    name: '',
    notes: '',
    dialogOpen: false,
    actioncode: '',

    superactivityOpen: false,
    superactivityLoading: false,
    superactivitySaving: false,
    superactivityModel: null,

    superactivityCreateOpen: false,
    superactivityCreateSaving: false,
  };

  componentDidMount() {
    void this.load();
  }
  
  private pci = () =>
    Number(this.props.plancontext?.pci ?? this.props.planningContext?.pci ?? 0);
  
  private load = async () => {
    const pci = this.pci();
    if (!pci) {
      this.setState({ error: 'Planning Cycle context is missing.' });
      return;
    }
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [model, lookups] = await Promise.all([
        fetchPpmWorkbench(pci, this.state.selectedId),
        fetchPpmLookups(pci).catch(() => ({})),
      ]);
      this.setState({
        model,
        lookups,
        selectedId: model.selectedPpm?.rid ?? null,
        name: model.selectedPpm?.nam ?? `PPM ${model.context.fiscalyear ?? ''}`,
      });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };
  
  private select = async (row: any) => {
    this.setState(
      { selectedId: row.rid, name: row.nam },
      () => void this.load()
    );
  };
  
  private generate = async () => {
    const m = this.state.model;
    const bvi = Number(m?.context.budgetversionid ?? 0);
    if (!bvi) {
      this.setState({
        error: 'An approved Budget Version is required before PPM generation.',
      });
      return;
    }
    this.setState({ saving: true, error: '', success: '' });
    try {
      await generateDraftPpm(this.pci(), bvi, this.state.name.trim());
      this.setState({
        success: 'Pre-approval PPM generated from the approved budget version.',
      });
      await this.load();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };
  
  private calendarize = async () => {
    const rid = this.state.model?.selectedPpm?.rid;
    if (!rid) return;
    this.setState({ saving: true, error: '', success: '' });
    try {
      await calendarizePpm(this.pci(), rid);
      this.setState({ success: 'PPM calendar generated.' });
      await this.load();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };
  
  private saveLines = async (rows: any[]) => {
    const rid = this.state.model?.selectedPpm?.rid;
    if (!rid) return;
    this.setState({ saving: true });
    try {
      await savePpmLines(this.pci(), rid, rows);
      this.setState({ success: 'PPM lines saved.' });
      await this.load();
    } finally {
      this.setState({ saving: false });
    }
  };
  
  private open = (actioncode: string) =>
    this.setState({ dialogOpen: true, actioncode, notes: '' });
  
  private transition = async () => {
    const ppm = this.state.model?.selectedPpm;
    if (!ppm) return;
    this.setState({ saving: true, error: '', success: '' });
    try {
      await transitionPpm(
        this.pci(),
        ppm.rid,
        this.state.actioncode,
        this.state.notes,
        ppm.stp
      );
      this.setState({
        dialogOpen: false,
        success: 'PPM transition completed.',
      });
      await this.load();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };

  createSuperactivity = async (
    model: PpmSuperactivityCreateModel
  ) => {
    const ppmid = this.state.model?.selectedPpm?.rid;

    if (!ppmid) {
      this.props.showNotification?.(
        'No PPM is currently selected.',
        'error'
      );
      return;
    }

    this.setState({
      superactivityCreateSaving: true,
    });

    try {
      const result = await createPpmSuperactivity(
        Number(ppmid),
        model.nam,
        model.shc,
        model.dsc,
        model.psc,
        model.pmd,
        model.pnt,
        model.rvt
      );

      this.setState({
        superactivityCreateOpen: false,
        superactivityCreateSaving: false,
      });

      await this.load();

      this.props.showNotification?.(
        'Superactivity created successfully.',
        'success'
      );

      /*
       * Optional UX improvement comes after qualification:
       * immediately open Manage Activities using result.rid.
       */
    } catch (error) {
      this.setState({
        superactivityCreateSaving: false,
      });

      this.props.showNotification?.(
        'Unable to create Superactivity.',
        'error'
      );
    }
  };

  manageSuperactivity = async (
    row: PpmSuperactivityRow
  ) => {
    this.setState({
      superactivityOpen: true,
      superactivityLoading: true,
      superactivityModel: null,
    });

    try {
      const model =
        await fetchPpmSuperactivityWorkbench(row.rid);

      this.setState({
        superactivityModel: model,
        superactivityLoading: false,
      });

    } catch (error) {
      this.setState({
        superactivityOpen: false,
        superactivityLoading: false,
      });

      this.props.showNotification?.(
        'Unable to load Superactivity.',
        'error'
      );
    }
  };

  saveSuperactivity = async (ids: number[]) => {
    const model = this.state.superactivityModel;

    if (!model) {
      return;
    }

    this.setState({
      superactivitySaving: true,
    });

    try {
      await assignPpmSuperactivityLines(
        model.superactivity.rid,
        ids
      );

      this.setState({
        superactivityOpen: false,
        superactivitySaving: false,
        superactivityModel: null,
      });

      await this.load();

      this.props.showNotification?.(
        'Superactivity updated successfully.',
        'success'
      );
    } catch (error) {
      this.setState({
        superactivitySaving: false,
      });

      this.props.showNotification?.(
        'Unable to update Superactivity.',
        'error'
      );
    }
  };
  
  render() {
    const m = this.state.model;
    const a = m?.actions;
    const actions = (
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          disabled={!a?.canGenerate}
          onClick={this.generate}
        >
          Generate Draft
        </Button>
        <Button
          variant="outlined"
          disabled={!a?.canCalendarize}
          onClick={this.calendarize}
        >
          Calendarize
        </Button>
        <Button
          variant="outlined"
          disabled={!a?.canSubmit}
          onClick={() => this.open('SUBMIT')}
        >
          Submit with Budget
        </Button>
        <Button
          variant="outlined"
          disabled={!a?.canValidateDraft}
          onClick={() => this.open('VALIDATE_DRAFT')}
        >
          Validate Draft
        </Button>
        <Button
          variant="contained"
          disabled={!a?.canFinalize}
          onClick={() => this.open('FINALIZE')}
        >
          Finalize PPM
        </Button>
      </Stack>
    );
  
    return (
      <PlanningWorkbenchShell
        {...this.props}
        title="Procurement Planning Control Center"
        subtitle="Prepare the pre-approval PPM from the approved budget, govern it with the budget, then publish the final adopted plan."
        icon={<ShoppingCartCheckoutOutlinedIcon />}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        actions={actions}
        onRefresh={this.load}
      >
        <Box sx={{ p: 3 }}>
          {m && !m.context.budgetversionid && (
            <Alert severity="info" sx={{ mb: 2 }}>
              PPM preparation is waiting for an approved Budget Version. Reports
              remain available for existing plans.
            </Alert>
          )}
          {m?.context.budgetversionid && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Source budget:{' '}
              <strong>
                {m.context.budgetversioncode || m.context.budgetversionname}
              </strong>{' '}
              · {m.context.budgetversionmilestone || 'APPROVED'}
            </Alert>
          )}
          <Tabs
            value={this.state.tab}
            onChange={(_, tab) => this.setState({ tab })}
            sx={{ mb: 2 }}
          >
            <Tab label="PPM Processing" />
            <Tab label="PPM Reports" />
          </Tabs>
          {this.state.tab === 0 && m && (
            <Stack spacing={3}>
              <PpmSummaryPanel cards={m.cards} />
              <PpmGrid
                rows={m.plans}
                selectedId={m.selectedPpm?.rid}
                loading={this.state.loading}
                onSelect={this.select}
              />

              <PpmSuperactivitiesPanel
                rows={m.superactivities || []}
                canCreate={
                  m.actions.canCreateSuperactivity
                }
                canManage={
                  m.actions.canManageSuperactivities
                }
                onCreate={() =>
                  this.setState({
                    superactivityCreateOpen: true,
                  })
                }
                onManage={this.manageSuperactivity}
              />
              {/* <PpmLinesGrid
                rows={m.lines}
                loading={this.state.loading}
                readOnly={!a?.canEdit}
                onChange={(lines) => this.setState({ model: { ...m, lines } })}
                onSubmit={this.saveLines}
              /> */}

              <PpmLinesGrid
                rows={m.lines}
                lookups={this.state.lookups}
                loading={this.state.loading}
                readOnly={!a?.canEdit}
                onChange={(lines) => this.setState({ model: { ...m, lines } })}
                onSubmit={this.saveLines}
              />
            </Stack>
          )}
          {this.state.tab === 1 && (
            <PpmReportWorkbench
              embedded
              planningcycleid={this.pci()}
              ppmid={m?.selectedPpm?.rid ?? null}
            />
          )}
          <Dialog
            open={this.state.dialogOpen}
            onClose={() => this.setState({ dialogOpen: false })}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              {this.state.actioncode.replaceAll('_', ' ')}
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                sx={{ mt: 1 }}
                label="Decision Notes"
                value={this.state.notes}
                onChange={(e) => this.setState({ notes: e.target.value })}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ dialogOpen: false })}>
                Cancel
              </Button>
              <Button variant="contained" onClick={this.transition}>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          <PpmSuperactivityCreateDialog
            open={this.state.superactivityCreateOpen}
            saving={this.state.superactivityCreateSaving}
            onClose={() =>
              this.setState({
                superactivityCreateOpen: false,
              })
            }
            onSave={this.createSuperactivity}
          />

          <PpmSuperactivityDialog
            open={this.state.superactivityOpen}
            loading={this.state.superactivityLoading}
            saving={this.state.superactivitySaving}
            model={this.state.superactivityModel}
            onClose={() =>
              this.setState({
                superactivityOpen: false,
                superactivityModel: null,
              })
            }
            onSave={this.saveSuperactivity}
          />
        </Box>
      </PlanningWorkbenchShell>
    );
  }
}
